'use client';

import { useTransCommon, useTransTournament } from '@/@shared/hooks/useTranslations';
import Icon from '@/Icon';
import { addLayer, deleteLayer } from '@/legacy/components/apps/ClientModalProvider';
import AppDownModal from '@/legacy/components/modalContents/AppDownModal';
import { StickerMap, tournamentV2DTO } from '@/legacy/components/new/tournament';
import styles from '@/legacy/components/new/tournamentLive.module.scss';
import OpenCard from '@/legacy/components/tournament/OpenCard';
import { YYYYMMDDHHmm } from '@/utilities/date';
import { nullCashReplace, nullCasualReplace } from '@/utilities/stateTool';

// Import domains API
import { useFetchV2TournamentSpecificStatusQuery } from '@/domains/content/api/v2_tournament/useV2_tournamentQueries';

const DoneBackground = () => {
	return (
		<picture className={styles.back}>
			<source media="(min-width: 769px)" srcSet="/back/done-background.png" />
			<img alt="done Trophy" loading="lazy" src="/back/m-done-background.png" />
		</picture>
	);
};
const LiveBackground = () => {
	return (
		<picture className={styles.back}>
			<source media="(min-width: 769px)" srcSet="/back/live-background.png" />
			<img alt="live Trophy" loading="lazy" src="/back/m-live-background.png" />
		</picture>
	);
};

type Props = {
	title: string;
	description: string;
	status: 'LIVE' | 'DONE';
};

const options = {
	DONE: {
		icon: 'Sticker_Status_Done',
		text: 'finishedInquiry',
	},
	LIVE: {
		icon: 'Sticker_Status_Live',
		text: 'liveInquiry',
	},
	OPEN: {
		icon: 'Sticker_Status_Open',
		text: 'exploreInquiry',
	},
} as const;

/** 모바일 연결 모달 */
export const openAppDownModal = (status: keyof typeof options, t: any) => {
	const textName = options[status].text;
	const icon = options[status].icon;

	addLayer(
		'AppDownload',
		<AppDownModal
			className={styles.modal}
			closeFn={() => {
				deleteLayer('AppDownload');
			}}
			QRBottomDescription
			titleComp={
				<div className={styles.modalTitle}>
					<Icon className={styles.modalIcon} path={icon} />
					<span dangerouslySetInnerHTML={{ __html: t(textName, { b: (chunks: any) => `<b>${chunks}</b>` }) }} />
				</div>
			}
		/>,
		{
			freeze: true,
			outsideDismiss: true,
		}
	);
};

const TournamentLive = ({ title, description, status }: Props) => {
	const t = useTransTournament('temp.reward');
	const modalText = useTransCommon('temp.modal');

	const { data, error, isLoading } = useFetchV2TournamentSpecificStatusQuery({
		query: {
			limit: 4,
			page: 1,
			status: status === 'LIVE' ? 'LIVE' : 'FINISHED',
		},
	});

	const tournaments = Array.isArray(data?.data) ? data.data : [];

	// 에러 상태 처리 - 조용히 숨김
	if (error) {
		return <></>;
	}

	if (tournaments.length > 0) {
		return (
			<div className={styles.horizontalSection}>
				<div className={styles.horizontalContainer}>
					{status === 'DONE' ? <DoneBackground /> : <LiveBackground />}

					<Icon className={styles.horizontalLive} path={StickerMap[status]} />
					<span className={styles.horizontalTitle}>{title}</span>
					<span className={styles.horizontalDescription}>{description}</span>
				</div>
				{tournaments.map((i, index) => {
					const { isDiamond, isRound, isPrivate, diaReward } = tournamentV2DTO(i, t);

					const src = isDiamond
						? nullCasualReplace(i.tournament.posterImageUrl)
						: nullCashReplace(i.tournament.posterImageUrl);
					return (
						<OpenCard
							allRound={'R ' + i.tournament.capacity}
							key={i.tournament.id}
							live={status === 'LIVE'}
							nation="nation"
							onClick={() => openAppDownModal(status, modalText)}
							private
							profileSrc={src}
							// @ts-ignore
							reword={diaReward}
							round={isRound ? i.round?.roundType! : YYYYMMDDHHmm(new Date(i.tournament.startSchedule))}
							title={i.tournament.title}
							type={isDiamond ? 'diamond' : 'gold'}
						/>
					);
				})}
			</div>
		);
	}

	return <></>;
};

export default TournamentLive;
